import Vue from 'vue'
import { ref, computed, watchEffect, watch } from '@vue/composition-api'
import TiposDeCanaisEnum from '@/models/entities/canal/TiposDeCanaisEnum'
import TicketCategoriaStatusEnum from '@/models/entities/ticket/TicketCategoriaStatusEnum'
import ChatBotProvedorComunicacaoEnum from '@/models/entities/canal/ChatBotProvedorComunicacaoEnum'
import ChatBotService from '@/services/modules/ChatBotService'
import FilaService from '@/services/modules/FilaService'
import TicketMensagemTemplateWhatsApp24HorasProps from '@/models/props/TicketMensagemTemplateWhatsApp24HorasProps'
import { getDateDifferenceInHours } from '@/utils/dateDifference'

export default function useTicketWhatsAppOuBotAlerta24Horas(ticketData, mensagens) {
  const MENSAGEM_DE_OBRIGATORIEDADE_DO_ENVIO_DO_TEMPLATE_DE_24_HORAS = 'A última mensagem desta conversa ultrapassou 24 horas. Para interagir novamente com este solicitante, clique em "Enviar template" para enviar a mensagem padrão aprovada e aguarde a interação do destinatário.'
  const MENSAGEM_DE_PROIBICAO_DO_ENVIO_DE_MENSAGEM_DO_ATENDENTE_PELO_FATO_DE_O_SOLICITANTE_AINDA_NAO_TER_RESPONDIDO_O_TEMPLATE_DE_24_HORAS = 'A última mensagem desta conversa foi um template de 24 horas, portanto é necessário esperar que o solicitante responda.'

  const ticketEstaFinalizado = computed(() => ticketData.value.categoriaStatusId === TicketCategoriaStatusEnum.Finalizado)

  const ticketFoiCriadoPorWhatsApp = computed(() => ticketData.value.canalTipoId === TiposDeCanaisEnum.WhatsApp)
  const ticketFoiCriadoPorBotEWhatsApp = computed(() => ticketData.value.canalTipoId === TiposDeCanaisEnum.Bot && mensagens.value[0]?.chatBotProvedorComunicacaoId === ChatBotProvedorComunicacaoEnum.WhatsApp)

  const ultimaMensagem = computed(() => mensagens.value.slice(-1)[0])

  const ultimaMensagemEhUmTemplateDeWhatsApp = computed(() => !!ultimaMensagem.value?.isTemplateWhatsApp)
  const proibirEnvioDeMensagemDoAtendentePeloFatoDeAUltimaMensagemSerUmTemplateDe24Horas = computed(() => ultimaMensagemEhUmTemplateDeWhatsApp.value)

  const ultimaMensagemFoiEnviadaAMaisDe24Horas = computed(() => (ultimaMensagem.value
    ? getDateDifferenceInHours(new Date(ultimaMensagem.value.dataCadastro), new Date()) >= 24
    : false))

  const canalDaFilaPermiteNotificarObrigatoriedadeDoEnvioDeTemplate24Horas = ref(false)
  const chatBotPermiteNotificarObrigatoriedadeDoEnvioDeTemplate24Horas = ref(false)
  const podeNotificarObrigatoriedadeDoEnvioDoTemplateDe24Horas = computed(() => canalDaFilaPermiteNotificarObrigatoriedadeDoEnvioDeTemplate24Horas.value || chatBotPermiteNotificarObrigatoriedadeDoEnvioDeTemplate24Horas.value)

  const notificarObrigatoriedadeDoEnvioDoTemplateDe24Horas = computed(() => (ticketFoiCriadoPorWhatsApp.value || ticketFoiCriadoPorBotEWhatsApp.value)
    && ultimaMensagemFoiEnviadaAMaisDe24Horas.value
    && podeNotificarObrigatoriedadeDoEnvioDoTemplateDe24Horas.value)

  watchEffect(async () => {
    if (ticketEstaFinalizado.value) return

    if (ticketFoiCriadoPorWhatsApp.value) {
      const filaCanalConfigWhatsApp = await FilaService.getCanalConfigWhatsApp(ticketData.value.filaId, ticketData.value.canalId)
      const permiteAlertaTemplate24Horas = filaCanalConfigWhatsApp.permitirAlertaTemplate

      canalDaFilaPermiteNotificarObrigatoriedadeDoEnvioDeTemplate24Horas.value = filaCanalConfigWhatsApp && permiteAlertaTemplate24Horas

      if (notificarObrigatoriedadeDoEnvioDoTemplateDe24Horas.value) {
        Vue.$toast.warning(MENSAGEM_DE_OBRIGATORIEDADE_DO_ENVIO_DO_TEMPLATE_DE_24_HORAS, { timeout: false })
      }
    }
  })

  watch(
    () => ticketFoiCriadoPorBotEWhatsApp.value,
    async v => {
      if (ticketEstaFinalizado.value) return

      if (v) {
        const chatBotConfigWhatsApp = await ChatBotService.getConfigWhatsApp(ticketData.value.chatBotId)
        const permiteAlertaTemplate24Horas = chatBotConfigWhatsApp.permitirAlertaTemplate

        chatBotPermiteNotificarObrigatoriedadeDoEnvioDeTemplate24Horas.value = chatBotConfigWhatsApp && permiteAlertaTemplate24Horas

        if (notificarObrigatoriedadeDoEnvioDoTemplateDe24Horas.value) {
          Vue.$toast.warning(MENSAGEM_DE_OBRIGATORIEDADE_DO_ENVIO_DO_TEMPLATE_DE_24_HORAS, { timeout: false })
        }
      }
    },
  )

  const templateWhatsApp24HorasProps = computed(() => (new TicketMensagemTemplateWhatsApp24HorasProps({
    notificarObrigatoriedadeDoEnvioDoTemplateDe24Horas: notificarObrigatoriedadeDoEnvioDoTemplateDe24Horas.value,
    mensagemDeObrigatoriedadeDoEnvioDoTemplateDe24Horas: MENSAGEM_DE_OBRIGATORIEDADE_DO_ENVIO_DO_TEMPLATE_DE_24_HORAS,
    proibirEnvioDeMensagemDoAtendentePeloFatoDeAUltimaMensagemSerUmTemplateDe24Horas: proibirEnvioDeMensagemDoAtendentePeloFatoDeAUltimaMensagemSerUmTemplateDe24Horas.value,
    mensagemDeProibicaoDoEnvioDeMensagemDoAtendentePeloFatoDeOSolicitanteAindaNaoTerRespondidoOTemplateDe24Horas: MENSAGEM_DE_PROIBICAO_DO_ENVIO_DE_MENSAGEM_DO_ATENDENTE_PELO_FATO_DE_O_SOLICITANTE_AINDA_NAO_TER_RESPONDIDO_O_TEMPLATE_DE_24_HORAS,
  })))

  return {
    templateWhatsApp24HorasProps,
  }
}
